import {
  CITY_ORDER,
  CITY_CENTROIDS,
  CITY_MAX_DISTANCE_KM,
  DEFAULT_GEO_FALLBACK_CITY,
} from '../config/cities';

export const haversineCoreRad = (lat1, lon1, lat2, lon2) => {
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  return 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const haversineDistanceKm = (lat1, lon1, lat2, lon2) => {
  if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) return null;
  const R = 6371;
  const c = haversineCoreRad(lat1, lon1, lat2, lon2);
  return parseFloat((R * c).toFixed(1));
};

export const haversineDistanceMeters = (lat1, lon1, lat2, lon2) => {
  if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) return null;
  const Rm = 6371000;
  const c = haversineCoreRad(lat1, lon1, lat2, lon2);
  return Rm * c;
};

export const getVenueCoords = (v) => {
  const lat = v.latitude ?? v.lat ?? v.location?.lat ?? v.location?.latitude;
  const lng = v.longitude ?? v.lng ?? v.location?.lng ?? v.location?.longitude;
  if (lat == null || lng == null) return null;
  const la = Number(lat);
  const ln = Number(lng);
  if (Number.isNaN(la) || Number.isNaN(ln)) return null;
  return { lat: la, lng: ln };
};

/** Sort key in km: GPS vs user when possible, else venue.distance / distance_km from API */
export const getVenueSortDistanceKm = (venue, userLoc) => {
  const coords = getVenueCoords(venue);
  if (userLoc?.lat != null && userLoc?.lng != null && coords) {
    return haversineDistanceKm(userLoc.lat, userLoc.lng, coords.lat, coords.lng);
  }
  const d = venue.distance ?? venue.distance_km ?? venue.distanceKm;
  if (d != null && Number.isFinite(Number(d))) return Number(d);
  return null;
};

export const compareDistanceTiebreak = (a, b, userLoc) => {
  const da = getVenueSortDistanceKm(a, userLoc);
  const db = getVenueSortDistanceKm(b, userLoc);
  if (da == null && db == null) return 0;
  if (da == null) return 1;
  if (db == null) return -1;
  return da - db;
};

/**
 * Picks the nearest `CITY_ORDER` market; if outside `CITY_MAX_DISTANCE_KM`, uses default.
 */
export function pickNearestLiveCity(userLat, userLng) {
  if (userLat == null || userLng == null || Number.isNaN(userLat) || Number.isNaN(userLng)) {
    return {
      city: DEFAULT_GEO_FALLBACK_CITY,
      isFallback: true,
      reason: 'no_coords',
      nearest: null,
      nearestDistanceKm: null,
    };
  }
  let nearest = null;
  let nearestKm = Infinity;
  for (const name of CITY_ORDER) {
    const c = CITY_CENTROIDS[name];
    if (!c) continue;
    const km = haversineDistanceKm(userLat, userLng, c.lat, c.lng);
    if (km != null && km < nearestKm) {
      nearestKm = km;
      nearest = name;
    }
  }
  if (nearest == null) {
    return {
      city: DEFAULT_GEO_FALLBACK_CITY,
      isFallback: true,
      reason: 'no_centroid',
      nearest: null,
      nearestDistanceKm: null,
    };
  }
  if (nearestKm > CITY_MAX_DISTANCE_KM) {
    return {
      city: DEFAULT_GEO_FALLBACK_CITY,
      isFallback: true,
      reason: 'outside_radius',
      nearest,
      nearestDistanceKm: nearestKm,
    };
  }
  return {
    city: nearest,
    isFallback: false,
    reason: 'nearest',
    nearest,
    nearestDistanceKm: nearestKm,
  };
}
